/**
 * Publication eligibility vs readiness — Phase 1 publish safety.
 *
 * Borraq keeps FIVE independent listing states. A READY content/readiness
 * result never authorizes publication on its own:
 *
 *   A. content + marketplace data readiness   (evaluate_listing_readiness)
 *   B. approval of the effective listing      (approved_version / operator_revision)
 *   C. connector authorization                (connector auth state)
 *   D. remote reconciliation / outcome        (publication_status)
 *   E. whether a NEW publish request is allowed right now
 *
 * This module only DERIVES presentation from canonical persisted columns. It
 * never recomputes readiness, never fabricates a remote outcome, and never
 * replaces server authority — `request_listing_publication` re-evaluates
 * readiness under lock and the worker claim fence remains the final authority.
 */

import type { ApprovalState } from "./channels";

/**
 * Mirrors `canPublish()` in src/lib/channels.ts (draft/ready/paused/error/drift).
 * Kept local + duplicated deliberately so this module stays dependency-free;
 * listing-publication-gate.test.ts asserts parity against channels.ts so the
 * two can never drift silently.
 */
export const PUBLISHABLE_LISTING_STATUSES = ["draft", "ready", "paused", "error", "drift"] as const;

export type PublicationGateCode =
  | "reconciliation_required"
  | "publication_in_progress"
  | "listing_closed"
  | "connector_not_authorized"
  | "approval_required"
  | "approval_stale"
  | "readiness_blocked";

export interface PublicationGateInput {
  status?: string | null;
  publicationStatus?: string | null;
  connectionReady: boolean;
  approval: ApprovalState;
  /** Persisted readiness status (listing_readiness_checks.status). */
  readinessStatus?: string | null;
  /** Blocking check count when known. */
  readinessBlocking?: number | null;
}

export interface PublicationGateResult {
  allowed: boolean;
  code: PublicationGateCode | null;
  /** Short badge label. */
  label: string;
  /** One sentence naming the specific blocker. */
  message: string;
  tone: "ok" | "warn" | "bad" | "neutral";
}

const BLOCK_COPY: Record<PublicationGateCode, { label: string; message: string; tone: "warn" | "bad" | "neutral" }> = {
  reconciliation_required: {
    label: "Reconciliation required",
    message:
      "The last publication attempt has no provider-confirmed outcome. A new publish request is blocked until the remote listing is reconciled.",
    tone: "bad",
  },
  publication_in_progress: {
    label: "Publication in progress",
    message: "A publication attempt is still running. Wait for the worker to finish before requesting another.",
    tone: "warn",
  },
  listing_closed: {
    label: "Not publishable",
    message: "This listing cannot be published from its current status.",
    tone: "neutral",
  },
  connector_not_authorized: {
    label: "Not connected",
    message: "The channel account is not authorized, so publication cannot proceed.",
    tone: "warn",
  },
  approval_required: {
    label: "Approval required",
    message: "The current listing content has not been approved for publication.",
    tone: "warn",
  },
  approval_stale: {
    label: "Approval out of date",
    message: "Operator-reviewable content changed after the last approval, so approval must be renewed first.",
    tone: "warn",
  },
  readiness_blocked: {
    label: "Not ready",
    message: "Readiness has blocking checks, so publication cannot proceed.",
    tone: "bad",
  },
};

function blocked(code: PublicationGateCode): PublicationGateResult {
  const copy = BLOCK_COPY[code];
  return { allowed: false, code, label: copy.label, message: copy.message, tone: copy.tone };
}

/**
 * Effective publication eligibility. Precedence follows the governed lifecycle:
 * unresolved remote outcome and terminal states outrank advisory UI states,
 * because a retry while the outcome is unknown is the unsafe action.
 */
export function publicationGate(input: PublicationGateInput): PublicationGateResult {
  const status = String(input.status ?? "");
  const publicationStatus = String(input.publicationStatus ?? "");

  if (status === "publishing" || publicationStatus === "publishing") {
    return blocked("publication_in_progress");
  }
  if (publicationStatus === "remote_review") {
    return blocked("reconciliation_required");
  }
  if (!(PUBLISHABLE_LISTING_STATUSES as readonly string[]).includes(status)) {
    return blocked("listing_closed");
  }
  if (!input.connectionReady) {
    return blocked("connector_not_authorized");
  }
  if (input.approval === "none") {
    return blocked("approval_required");
  }
  if (input.approval === "stale") {
    return blocked("approval_stale");
  }
  if ((input.readinessBlocking ?? 0) > 0 || String(input.readinessStatus ?? "") === "BLOCKED") {
    return blocked("readiness_blocked");
  }
  return {
    allowed: true,
    code: null,
    label: "Ready to publish",
    message:
      "Readiness, approval, connector and remote outcome are all clear. Publishing will still be re-verified by the server.",
    tone: "ok",
  };
}

export type PublicationStateRowKey = "readiness" | "approval" | "connector" | "remote" | "eligibility";

export interface PublicationStateRow {
  key: PublicationStateRowKey;
  label: string;
  value: string;
  tone: "ok" | "warn" | "bad" | "neutral";
  note: string | null;
}

const READINESS_VALUES: Record<string, string> = {
  READY: "Ready",
  READY_WITH_WARNINGS: "Warnings",
  BLOCKED: "Blocked",
  NOT_EVALUATED: "Not evaluated",
  NEEDS_MAPPING: "Needs mapping",
};

function readinessRowValue(status?: string | null): { value: string; tone: PublicationStateRow["tone"] } {
  const s = String(status ?? "");
  if (s === "READY") return { value: "Ready", tone: "ok" };
  if (s === "READY_WITH_WARNINGS") return { value: "Warnings", tone: "warn" };
  if (s === "BLOCKED") return { value: "Blocked", tone: "bad" };
  return { value: READINESS_VALUES[s] ?? "Not evaluated", tone: "neutral" };
}

function remoteOutcomeRowValue(input: PublicationGateInput): { value: string; tone: PublicationStateRow["tone"] } {
  const publicationStatus = String(input.publicationStatus ?? "");
  if (publicationStatus === "remote_review") return { value: "Reconciliation required", tone: "bad" };
  if (publicationStatus === "publishing") return { value: "Publication in progress", tone: "warn" };
  if (publicationStatus === "published") return { value: "Provider-confirmed published", tone: "ok" };
  if (publicationStatus === "drift") return { value: "Provider drift detected", tone: "warn" };
  if (publicationStatus === "sync_failed" || publicationStatus === "error" || String(input.status ?? "") === "error") {
    return { value: "Publication failed — no confirmed remote outcome", tone: "bad" };
  }
  if (publicationStatus === "unpublished" || publicationStatus === "cancelled") {
    return { value: "Not published", tone: "neutral" };
  }
  return { value: "Not published yet", tone: "neutral" };
}

/**
 * The five independent states, rendered separately so a READY readiness result
 * can never be mistaken for publication authorization.
 */
export function listingPublicationStates(
  input: PublicationGateInput & { approvedVersion?: number | null },
): PublicationStateRow[] {
  const readiness = readinessRowValue(input.readinessStatus);
  const remote = remoteOutcomeRowValue(input);
  const gate = publicationGate(input);
  const approved = input.approvedVersion;

  return [
    {
      key: "readiness",
      label: "Content & data readiness",
      value: readiness.value,
      tone: readiness.tone,
      note: "Required, but not sufficient — it never authorizes publication on its own.",
    },
    {
      key: "approval",
      label: "Listing approval",
      value:
        input.approval === "approved"
          ? `Approved${approved ? ` v${approved}` : ""}`
          : input.approval === "stale"
            ? "Approval out of date"
            : "Approval pending",
      tone: input.approval === "approved" ? "ok" : input.approval === "stale" ? "warn" : "neutral",
      note: "Approval covers operator-reviewable content only; price and inventory sync automatically.",
    },
    {
      key: "connector",
      label: "Channel account",
      value: input.connectionReady ? "Authorized" : "Not authorized",
      tone: input.connectionReady ? "ok" : "warn",
      note: null,
    },
    {
      key: "remote",
      label: "Remote outcome",
      value: remote.value,
      tone: remote.tone,
      note: remote.tone === "bad" ? "No provider-confirmed publication outcome is recorded." : null,
    },
    {
      key: "eligibility",
      label: "New publish request",
      value: gate.allowed ? "Permitted" : "Blocked",
      tone: gate.allowed ? "ok" : "bad",
      note: gate.allowed ? null : gate.message,
    },
  ];
}

export interface PublicationJobLike {
  status?: string | null;
  error_class?: string | null;
  error_message?: string | null;
  result?: Record<string, unknown> | null;
}

export interface PublicationJobDiagnostic {
  /** Actionable classification, or null when the evidence does not support one. */
  classification: string | null;
  tone: "bad" | "warn" | "neutral";
  /** Structured detail retained by the worker, including the publisher code. */
  detail: string | null;
  /** True when only a generic transport message survived. */
  detailLost: boolean;
}

const GENERIC_EDGE_MESSAGE = /non-2xx/i;

const ERROR_CLASS_LABELS: Record<string, string> = {
  retryable: "Transport or provider error — retryable",
  rate_limited: "Provider rate limit",
  permanent: "Request rejected before any provider write",
};

/**
 * Publication job diagnostics. Historical rows may only contain the generic
 * supabase-js transport message; in that case the original cause is genuinely
 * unknown and is reported as such rather than inferred.
 */
export function publicationJobDiagnostic(job: PublicationJobLike): PublicationJobDiagnostic {
  const result = job.result ?? {};
  const code = typeof result.code === "string" ? result.code : null;
  const httpStatus = result.http_status != null ? String(result.http_status) : null;
  const publisherError = typeof result.publisher_error === "string" ? result.publisher_error : null;
  const errorClass = String(job.error_class ?? "");
  const message = String(job.error_message ?? "");

  const detailParts = [
    code ? `code ${code}` : null,
    httpStatus ? `HTTP ${httpStatus}` : null,
    publisherError && publisherError !== message ? publisherError : null,
  ].filter(Boolean) as string[];
  const detail = detailParts.length ? detailParts.join(" · ") : null;

  if (String(job.status ?? "") === "remote_review" || errorClass === "uncertain_external_result" || code === "REMOTE_REVIEW") {
    return {
      classification: "Unknown remote outcome — reconciliation required",
      tone: "bad",
      detail,
      detailLost: false,
    };
  }
  if (errorClass === "auth" || httpStatus === "401" || httpStatus === "403") {
    return { classification: "Authentication or permission failure", tone: "bad", detail, detailLost: false };
  }
  if (errorClass === "not_configured") {
    return { classification: "Connector is not configured", tone: "bad", detail, detailLost: false };
  }
  if (code && code !== "REMOTE_REVIEW") {
    return { classification: `Publisher rejected the request (${code})`, tone: "bad", detail, detailLost: false };
  }
  if (ERROR_CLASS_LABELS[errorClass]) {
    return { classification: ERROR_CLASS_LABELS[errorClass], tone: "warn", detail, detailLost: false };
  }
  if (GENERIC_EDGE_MESSAGE.test(message) && !detail) {
    return {
      classification: null,
      tone: "warn",
      detail: null,
      detailLost: true,
    };
  }
  return { classification: null, tone: "neutral", detail, detailLost: false };
}

/** Honest copy for a job whose original error detail was never retained. */
export function lostDetailNote(): string {
  return "The original error detail was not retained for this attempt, so the historical cause is unknown.";
}

export interface PublicationNoticeModel {
  gate: PublicationGateResult;
  states: PublicationStateRow[];
}

export function publicationNoticeModel(
  input: PublicationGateInput & { approvedVersion?: number | null },
): PublicationNoticeModel {
  return { gate: publicationGate(input), states: listingPublicationStates(input) };
}
